/** Trajectoire : SVG inline, années réalisées en trait plein, projection « si rien ne change » en pointillés. */
import type { YearState } from "../../../engine/src/types.js";
import { num, pct } from "../format.js";
import type { OutlookPoint } from "../outlook.js";

interface Series {
  key: string;
  label: string;
  color: string;
  dec: number;
}

const SERIES: Series[] = [
  { key: "debt_pct_gdp", label: "dette / PIB (%)", color: "var(--cat-2)", dec: 0 },
  { key: "deficit_pct_gdp", label: "déficit / PIB (%)", color: "var(--cat-1)", dec: 1 },
  { key: "confidence", label: "confiance des marchés", color: "var(--cat-3)", dec: 0 },
];

const W = 600;
const H = 110;
const PAD_X = 24;
const PAD_Y = 12;

export function chartHtml(realised: YearState[], projection: YearState[]): string {
  const all = [...realised, ...projection];
  if (all.length < 2) return `<p class="mut sm">La trajectoire apparaîtra après la première année.</p>`;
  const n = all.length - 1;
  const xs = (i: number) => PAD_X + (i * (W - 2 * PAD_X)) / n;
  const lines = SERIES.map((s) => {
    const vals = all.map((y) => y.kpis[s.key]).filter((v): v is number => v !== undefined);
    if (vals.length !== all.length) return "";
    let mn = Math.min(...vals);
    let mx = Math.max(...vals);
    if (mx - mn < 1) {
      mn -= 0.5;
      mx += 0.5;
    }
    const pad = (mx - mn) * 0.15;
    mn -= pad;
    mx += pad;
    const py = (v: number) => H - PAD_Y - ((v - mn) / (mx - mn)) * (H - 2 * PAD_Y);
    const pts = (ys: YearState[], from: number) => ys.map((y, i) => `${xs(from + i).toFixed(1)},${py(y.kpis[s.key]!).toFixed(1)}`).join(" ");
    const solid = `<polyline fill="none" stroke="${s.color}" stroke-width="2" points="${pts(realised, 0)}"/>`;
    const dashed = projection.length
      ? `<polyline fill="none" stroke="${s.color}" stroke-width="2" stroke-dasharray="4 4" opacity=".7" points="${pts([realised[realised.length - 1]!, ...projection], realised.length - 1)}"/>`
      : "";
    return solid + dashed;
  }).join("");
  const labels = all.map((y, i) => `<text x="${xs(i).toFixed(1)}" y="${H - 1}" font-size="9" fill="var(--mut)" text-anchor="middle">${y.year}</text>`).join("");
  const legend = SERIES.map((s) => `<span><i style="background:${s.color}"></i>${s.label}</span>`).join(" &nbsp; ");
  const last = projection[projection.length - 1];
  const endTxt = last
    ? `<p class="hint">Fin de mandat projetée si rien ne change : déficit ${last.kpis.deficit_pct_gdp !== undefined ? pct(last.kpis.deficit_pct_gdp) : "—"}, dette ${last.kpis.debt_pct_gdp !== undefined ? pct(last.kpis.debt_pct_gdp, 0) : "—"}, chômage ${last.kpis.unemployment_pct !== undefined ? pct(last.kpis.unemployment_pct) : "—"}.</p>`
    : "";
  return `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Trajectoire du mandat : ${SERIES.map((s) => s.label).join(", ")}">${lines}${labels}</svg>
  <p class="legend">${legend}${projection.length ? ` &nbsp; <span style="opacity:.7">┄ projection si rien ne change</span>` : ""}</p>${endTxt}
  <p class="hint">Chaque courbe a sa propre échelle (min–max de la série) : lis les tendances, pas les niveaux croisés. Valeurs : ${SERIES.map((s) => {
    const v = all[all.length - 1]!.kpis[s.key];
    return v === undefined ? "" : `${s.label} ${num(v, s.dec)}`;
  })
    .filter(Boolean)
    .join(" · ")}.</p>`;
}

/* ------------------------------------------------------------------ trajectoire comparée à la trajectoire officielle */

export interface PathSeries {
  label: string;
  points: Array<{ year: number; value: number }>;
  /** `solid` = ta partie, `dashed` = « si rien ne change ». */
  style: "solid" | "dashed";
  color: string;
}

const CW = 320;
const CH = 150;
const CL = 34;
const CR = 18;
const CT = 12;
const CB = 22;

/**
 * Un indicateur, une échelle commune (contrairement à `chartHtml`) : ta trajectoire en trait plein, la trajectoire
 * officielle en pointillés, la fourchette officielle (low/high) en bande. `fmt` formate les graduations.
 */
export function compareChart(title: string, series: PathSeries[], official: OutlookPoint[] | undefined, officialLabel: string, fmt: (v: number) => string): string {
  const all = [...series.flatMap((s) => s.points), ...(official ?? []).flatMap((p) => [{ year: p.year, value: p.value }, ...(p.low !== undefined ? [{ year: p.year, value: p.low }] : []), ...(p.high !== undefined ? [{ year: p.year, value: p.high }] : [])])];
  if (all.length < 2) return "";
  const years = [...new Set(all.map((p) => p.year))].sort((a, b) => a - b);
  const y0 = years[0]!;
  const y1 = years[years.length - 1]!;
  let mn = Math.min(...all.map((p) => p.value));
  let mx = Math.max(...all.map((p) => p.value));
  if (mx - mn < 1) {
    mn -= 0.5;
    mx += 0.5;
  }
  const pad = (mx - mn) * 0.1;
  mn -= pad;
  mx += pad;
  const px = (y: number) => CL + ((y - y0) / Math.max(1, y1 - y0)) * (CW - CL - CR);
  const py = (v: number) => CT + (1 - (v - mn) / (mx - mn)) * (CH - CT - CB);
  const path = (pts: Array<{ year: number; value: number }>) => pts.map((p) => `${px(p.year).toFixed(1)},${py(p.value).toFixed(1)}`).join(" ");
  const off = official ?? [];
  const band = off.filter((p) => p.low !== undefined && p.high !== undefined);
  const bandPoly =
    band.length >= 1
      ? `<polygon points="${[...band.map((p) => `${px(p.year).toFixed(1)},${py(Math.max(p.low!, p.high!)).toFixed(1)}`), ...[...band].reverse().map((p) => `${px(p.year).toFixed(1)},${py(Math.min(p.low!, p.high!)).toFixed(1)}`)].join(" ")}" fill="var(--txt)" opacity=".10"${band.length === 1 ? ` stroke="var(--txt)" stroke-opacity=".3" stroke-width="6"` : ""}/>`
      : "";
  const offLine = off.length
    ? `${off.length > 1 ? `<polyline fill="none" stroke="var(--txt)" stroke-width="1.6" stroke-dasharray="2 3" points="${path(off)}"/>` : ""}${off.map((p) => `<circle cx="${px(p.year).toFixed(1)}" cy="${py(p.value).toFixed(1)}" r="2.6" fill="var(--txt)"/>`).join("")}`
    : "";
  const lines = series
    .filter((s) => s.points.length)
    .map((s) => `<polyline fill="none" stroke="${s.color}" stroke-width="2" ${s.style === "dashed" ? `stroke-dasharray="5 4" opacity=".7"` : ""} points="${path(s.points)}"/>`)
    .join("");
  const ticks = [mn + pad, (mn + mx) / 2, mx - pad]
    .map((v) => `<line x1="${CL}" x2="${CW - CR}" y1="${py(v).toFixed(1)}" y2="${py(v).toFixed(1)}" stroke="var(--line)" stroke-width=".6"/><text x="${CL - 3}" y="${(py(v) + 3).toFixed(1)}" font-size="9" fill="var(--mut)" text-anchor="end">${fmt(v)}</text>`)
    .join("");
  const step = years.length > 7 ? 2 : 1;
  const xl = years
    .filter((_, i) => i % step === 0 || i === years.length - 1)
    .map((y) => `<text x="${px(y).toFixed(1)}" y="${CH - 6}" font-size="9" fill="var(--mut)" text-anchor="middle">${y}</text>`)
    .join("");
  const legend = [
    ...series.filter((s) => s.points.length).map((s) => `<span><i style="background:${s.color}${s.style === "dashed" ? ";opacity:.6" : ""}"></i>${s.label}</span>`),
    ...(off.length ? [`<span><i class="dot"></i>${officialLabel}</span>`] : []),
    ...(band.length ? [`<span><i class="band"></i>fourchette publiée</span>`] : []),
  ].join(" ");
  return `<figure class="cmpchart"><figcaption class="sm"><b>${title}</b></figcaption>
    <svg class="chart" viewBox="0 0 ${CW} ${CH}" role="img" aria-label="${title} : ${series.map((s) => s.label).join(", ")}${off.length ? `, ${officialLabel}` : ""}">${ticks}${bandPoly}${offLine}${lines}${xl}</svg>
    <p class="legend">${legend}</p></figure>`;
}
